import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useApi } from '../../hooks/useApi';
import { Card, Loading, ModuleHero, Notice, Spinner, StatusPill, Tabs } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import GlossaryHint from '../../components/GlossaryHint';
import UploadButton from '../../components/UploadButton';
import ProgressTracker from '../../components/ProgressTracker';
import { errorMessage, openProtectedFile } from '../../lib/http';
import { DOC_CATEGORY, DOC_STATUS, TRACK_TONE, formatBytes, formatDateTime } from '../../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE, CARD, CARD_STYLE, FIELD, FIELD_STYLE } from '../../lib/ui';
import type { Classification, CompanyDocument, GlossaryTerm, JourneyProgress, Requirement } from '../../types/workspace';

const ITR_HINT = { term: 'What is ITR?', meaning: 'Income Tax Return — the annual tax filing that declares taxable income.' };
const GSTR_HINT = { term: 'What is GSTR?', meaning: 'Goods and Services Tax Return — periodic Indian indirect-tax filings that evidence reported turnover.' };
const TAX_HINTS: Record<string, { term: string; meaning: string }> = {
  TAX_RETURNS: ITR_HINT,
  PROMOTER_ITR: ITR_HINT,
  INDIRECT_TAX_RETURNS: GSTR_HINT,
};

const CATEGORY_ORDER = ['FINANCIAL', 'LEGAL', 'KYC', 'BUSINESS', 'OTHER'];
type Filter = 'all' | 'todo' | 'attention';

type DocsResponse = {
  companyStatus: string;
  classification: Classification;
  requirements: Requirement[];
  documents: CompanyDocument[];
  missing: Requirement[];
  progress: JourneyProgress;
  glossary: GlossaryTerm[];
  isDemo: boolean;
};

function DocSlot({
  requirement,
  doc,
  editable,
  onChanged,
  onError,
}: {
  requirement: Requirement;
  doc?: CompanyDocument;
  editable: boolean;
  onChanged: () => void;
  onError: (m: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const status = doc ? DOC_STATUS[doc.status] || { label: doc.status, tone: 'slate' as const } : null;

  const act = async (kind: 'verify' | 'delete') => {
    if (!doc) return;
    setBusy(kind);
    try {
      if (kind === 'verify') await api.post(`/customer/documents/${doc.id}/verify`);
      else await api.delete(`/customer/documents/${doc.id}`);
      onChanged();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const markNotApplicable = async () => {
    const reason = window.prompt(`Why is “${requirement.title}” not applicable?`);
    if (!reason?.trim()) return;
    setBusy('not-applicable');
    try {
      await api.post(`/customer/documents/${requirement.code}/not-applicable`, { reason: reason.trim() });
      onChanged();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className={`${CARD} p-4`} style={{ ...CARD_STYLE, borderColor: !doc && requirement.required ? '#f9c0d8' : '#fce7f3' }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-bold text-[#6b0030]">{requirement.title}</h3>
            {TAX_HINTS[requirement.code] && <GlossaryHint term={TAX_HINTS[requirement.code].term} meaning={TAX_HINTS[requirement.code].meaning} />}
            <StatusPill label={requirement.statusLabel || (requirement.required ? 'required' : 'optional')} tone={requirement.required ? 'pink' : requirement.statusLabel === 'conditional' ? 'amber' : 'slate'} />
          </div>
          {requirement.description && <p className="mt-0.5 text-xs text-[#c084a0]">{requirement.description}</p>}
          <div className="mt-2 space-y-1 text-xs leading-relaxed text-[#9a6b82]">
            {requirement.applicablePeriod && <p><strong>Period:</strong> {requirement.applicablePeriod}</p>}
            {requirement.acceptedFormats && <p><strong>Formats:</strong> {Array.isArray(requirement.acceptedFormats) ? requirement.acceptedFormats.join(', ') : requirement.acceptedFormats}</p>}
            {requirement.issuerOrSignature && <p><strong>Issuer / signature:</strong> {requirement.issuerOrSignature}</p>}
            {requirement.reason && <p><strong>Why needed:</strong> {requirement.reason}</p>}
            {requirement.example && <p><strong>Example:</strong> {requirement.example}</p>}
          </div>
        </div>
        {status && <StatusPill label={status.label} tone={status.tone} pulse={doc?.status === 'VERIFYING' || doc?.status === 'UPLOADED'} />}
      </div>

      {doc ? (
        <div className="mt-3 rounded-xl p-3" style={{ background: '#fffafc', border: `1px solid ${doc.status === 'WRONG_ENTITY' || doc.isSynthetic || doc.isPlaceholder ? '#fb7185' : '#fce7f3'}` }}>
          {(doc.status === 'WRONG_ENTITY' || doc.entityMatch === 'MISMATCH') && <div className="mb-3 rounded-lg bg-rose-50 p-2 text-xs font-bold text-rose-800">Wrong legal entity detected. Expected this workspace’s company{doc.extractedLegalName ? `; this file names ${doc.extractedLegalName}` : ''}. Replace this file before submission.</div>}
          {(doc.isSynthetic || doc.isPlaceholder) && <div className="mb-3 rounded-lg bg-amber-50 p-2 text-xs font-bold text-amber-800">{doc.isSynthetic ? 'Synthetic or generated document detected.' : 'Placeholder/sample document detected.'} Upload an authentic final document.</div>}
          {doc.status === 'NOT_APPLICABLE' && <div className="mb-3 rounded-lg bg-slate-50 p-2 text-xs text-slate-700"><strong>Marked not applicable:</strong> {doc.notApplicableReason}</div>}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button type="button" onClick={() => void openProtectedFile(`/customer/documents/${doc.id}/file`).catch(() => onError('Could not open file.'))} className="min-w-0 truncate text-left text-sm font-semibold text-[#d6006e] hover:underline">
              {doc.fileName}
            </button>
            <span className="text-xs text-[#c084a0]">
              {formatBytes(doc.sizeBytes)} · {formatDateTime(doc.createdAt)}
            </span>
          </div>
          {(doc.extractedFinancialYear || doc.extractedAuditor) && (
            <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#6b3a52]">
              {doc.extractedFinancialYear && (
                <div className="flex gap-1">
                  <dt className="font-bold text-[#9d0050]">Financial year read:</dt>
                  <dd>{doc.extractedFinancialYear}</dd>
                </div>
              )}
              {doc.extractedAuditor && (
                <div className="flex gap-1">
                  <dt className="font-bold text-[#9d0050]">Auditor read:</dt>
                  <dd>{doc.extractedAuditor}</dd>
                </div>
              )}
            </dl>
          )}
          {doc.aiSummary && (
            <p className="mt-2 text-xs leading-relaxed text-[#6b3a52]">
              <span className="font-bold text-[#9d0050]">AI{doc.aiConfidence != null ? ` · ${doc.aiConfidence}%` : ''}: </span>
              {doc.aiSummary}
            </p>
          )}
          {doc.aiIssues && doc.aiIssues.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {doc.aiIssues.map((i, n) => (
                <li key={n} className="text-xs text-[#be123c]">• {i}</li>
              ))}
            </ul>
          )}
          {doc.reviewNote && (
            <p className="mt-2 text-xs text-[#6b3a52]">
              <span className="font-bold text-[#9d0050]">Analyst: </span>
              {doc.reviewNote}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-3 text-xs text-[#c084a0]">Not uploaded yet. PDF or clear images verify fastest.</p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <UploadButton code={requirement.code} title={requirement.title} label={doc ? 'Replace' : 'Upload'} primary={!doc} onUploaded={onChanged} onError={onError} />
        {doc && !['VERIFYING', 'UPLOADED'].includes(doc.status) && (
          <button type="button" disabled={busy !== null} onClick={() => void act('verify')} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
            {busy === 'verify' ? <Spinner className="h-3.5 w-3.5" /> : null} Re-run AI check
          </button>
        )}
        {doc && editable && (
          <button type="button" disabled={busy !== null} onClick={() => void act('delete')} className="rounded-xl px-3 py-2 text-xs font-bold text-[#be123c] hover:bg-[#fff1f2]">
            Remove
          </button>
        )}
        {requirement.allowNotApplicable && doc?.status !== 'NOT_APPLICABLE' && (
          <button type="button" disabled={busy !== null} onClick={() => void markNotApplicable()} className="rounded-xl px-3 py-2 text-xs font-bold text-[#6b0030] hover:bg-[#fce7f3]">
            Not applicable
          </button>
        )}
      </div>
    </article>
  );
}

export default function Documents() {
  const [polling, setPolling] = useState(false);
  const { data, loading, error, code, reload } = useApi<DocsResponse>('/customer/documents', polling ? 4000 : undefined);
  const [notice, setNotice] = useState<{ tone: 'green' | 'rose'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [extraTitle, setExtraTitle] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const busyDocs = (data?.documents || []).some((d) => d.status === 'VERIFYING' || d.status === 'UPLOADED');
  useEffect(() => setPolling(busyDocs), [busyDocs]);

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const editable = ['INVITED', 'INTAKE'].includes(data.companyStatus);
  const docByCode = new Map(data.documents.map((d) => [d.code, d]));
  const requiredCount = data.requirements.filter((r) => r.required).length;
  const doneRequired = requiredCount - data.missing.length;
  const extras = data.documents.filter((d) => !data.requirements.some((r) => r.code === d.code));
  const pct = requiredCount ? Math.round((doneRequired / requiredCount) * 100) : 100;
  const needsAttention = (r: Requirement) => {
    const d = docByCode.get(r.code);
    return Boolean(d && ['NEEDS_REVIEW', 'REJECTED', 'WRONG_ENTITY', 'EXPIRED', 'ANALYST_REVIEW'].includes(d.status));
  };
  const visible = data.requirements.filter((r) =>
    filter === 'todo' ? !docByCode.has(r.code) : filter === 'attention' ? needsAttention(r) : true,
  );
  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    all: data.requirements.filter((r) => (r.category || 'OTHER') === cat),
    shown: visible.filter((r) => (r.category || 'OTHER') === cat),
  })).filter((g) => g.shown.length > 0);
  const cls = data.classification;

  const onError = (text: string) => setNotice({ tone: 'rose', text });
  const onChanged = () => {
    setNotice(null);
    void reload(true);
  };

  const submit = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post('/customer/submit');
      await reload(true);
    } catch (err) {
      onError(errorMessage(err, 'Could not submit.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitUpdates = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post('/customer/reanalysis');
      await reload(true);
    } catch (err) {
      onError(errorMessage(err, 'Could not submit for review.'));
    } finally {
      setSubmitting(false);
    }
  };

  const hasWrongEntity = data.documents.some((d) => d.status === 'WRONG_ENTITY' || d.entityMatch === 'MISMATCH');
  const submitBlocked = data.missing.length > 0 || !cls || busyDocs || hasWrongEntity;
  const inReview = data.companyStatus === 'SUBMITTED' || data.companyStatus === 'REANALYSIS';
  if (inReview) {
    const refreshing = data.companyStatus === 'REANALYSIS';
    return (
      <div>
        <ModuleHero
          eyebrow="Step 2 · Submit docs"
          title="Documents"
          description="Your pack is locked while your Micus analyst reviews it."
          actions={<StatusPill label="In review" tone="blue" pulse />}
        />
        <ProgressTracker progress={data.progress} />

        <div
          className="mz-fade-up mz-d1 mt-8 overflow-hidden rounded-3xl text-white"
          style={{
            background: 'linear-gradient(135deg, #0b3a7a 0%, #1d4ed8 48%, #3b82f6 100%)',
            boxShadow: '0 22px 48px rgba(29, 78, 216, 0.28)',
          }}
          role="status"
        >
          <div className="relative px-6 py-10 sm:px-10 sm:py-12">
            <div
              className="pointer-events-none absolute -right-10 -top-16 h-48 w-48 rounded-full opacity-30"
              style={{ background: 'radial-gradient(circle, #93c5fd 0%, transparent 70%)' }}
              aria-hidden
            />
            <div className="relative mx-auto max-w-xl text-center">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                <span className="mz-pulse-soft inline-block h-2 w-2 rounded-full bg-[#93c5fd]" aria-hidden />
                In review
              </p>
              <h2 className="mt-4 text-2xl font-black tracking-tight sm:text-3xl">
                {refreshing ? 'Your updated pack is with your analyst' : 'Your document pack is with your analyst'}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/85 sm:text-base">
                {refreshing
                  ? 'Submitted for review. Your Micus analyst will prepare a refreshed rating. Uploads stay locked until that rating is published.'
                  : 'Submitted for rating. Your Micus analyst is reviewing every file and will publish your credit rating next. Uploads stay locked until then.'}
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={refreshing ? '/rating' : '/overview'}
                  className="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#1d4ed8] shadow-lg"
                >
                  {refreshing ? 'View current rating' : 'Back to overview'} →
                </Link>
                <Link
                  to="/messages"
                  className="rounded-xl border border-white/35 bg-white/10 px-5 py-2.5 text-sm font-bold text-white backdrop-blur hover:bg-white/20"
                >
                  Message analyst
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ModuleHero
        eyebrow="Step 2 · Submit docs"
        title="Documents"
        description="The document pack below is matched to your country and legal entity type — the same list a rating agency asks for. Micus AI checks every file the moment it lands: wrong document type, name mismatches, expired or illegible pages."
        actions={
          editable ? (
            <button type="button" disabled={submitting || submitBlocked} onClick={() => void submit()} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE} title={hasWrongEntity ? 'Replace wrong-entity documents first' : undefined}>
              {submitting && <Spinner />} Submit for rating
            </button>
          ) : ['RATED', 'REMEDIATING'].includes(data.companyStatus) ? (
            <button type="button" disabled={submitting || submitBlocked} onClick={() => void submitUpdates()} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE} title={hasWrongEntity ? 'Replace wrong-entity documents first' : undefined}>
              {submitting && <Spinner />} Submit for review
            </button>
          ) : (
            <StatusPill label="Submitted — replace files any time" tone="blue" />
          )
        }
      />
      <ProgressTracker progress={data.progress} />

      <div className={`${CARD} mz-fade-up mz-d1 mt-6 p-4`} style={CARD_STYLE}>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-bold text-[#6b0030]">
            {doneRequired} of {requiredCount} required documents uploaded
          </span>
          {busyDocs && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1d4ed8]">
              <Spinner className="h-3 w-3" /> AI is verifying…
            </span>
          )}
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#fce7f3]">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#d6006e,#ff4fa2)' }} />
        </div>
      </div>

      {cls ? (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[#b0708f]">
          <StatusPill label={`${cls.track.label} track`} tone={TRACK_TONE[cls.track.code]} />
          <span>
            Pack for a <span className="font-bold text-[#6b0030]">{cls.type.label}</span> · {data.requirements.length} documents, {requiredCount} required
          </span>
        </div>
      ) : (
        <div className="mt-4">
          <Notice tone="amber">
            Only the universal documents are shown. <Link to="/company" className="font-bold underline">Set your company type</Link> to unlock the full pack for your
            rating track — you can't submit without it.
          </Notice>
        </div>
      )}

      {notice && (
        <div className="mt-4">
          <Notice tone={notice.tone}>{notice.text}</Notice>
        </div>
      )}

      <div className="mt-5">
        <Tabs<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'all', label: 'All documents', count: data.requirements.length },
            { id: 'todo', label: 'Not uploaded', count: data.requirements.filter((r) => !docByCode.has(r.code)).length },
            { id: 'attention', label: 'Needs attention', count: data.requirements.filter(needsAttention).length },
          ]}
        />
      </div>

      {groups.length === 0 && <p className="mt-5 text-sm text-[#c084a0]">Nothing here — nice work.</p>}

      {groups.map((g) => {
        const meta = DOC_CATEGORY[g.cat] || DOC_CATEGORY.OTHER;
        const uploaded = g.all.filter((r) => docByCode.has(r.code)).length;
        return (
          <section key={g.cat} className="mt-6">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2 border-b border-[#fce7f3] pb-2">
              <div>
                <h2 className="text-sm font-black uppercase tracking-[0.14em] text-[#6b0030]">{meta.label}</h2>
                <p className="text-xs text-[#c084a0]">{meta.hint}</p>
              </div>
              <span className="text-xs font-bold text-[#b0708f]">
                {uploaded}/{g.all.length} uploaded
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {g.shown.map((r) => (
                <DocSlot key={r.code} requirement={r} doc={docByCode.get(r.code)} editable={editable} onChanged={onChanged} onError={onError} />
              ))}
            </div>
          </section>
        );
      })}

      {extras.length > 0 && filter === 'all' && (
        <section className="mt-6">
          <h2 className="mb-3 border-b border-[#fce7f3] pb-2 text-sm font-black uppercase tracking-[0.14em] text-[#6b0030]">Additional documents</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {extras.map((d) => (
              <DocSlot
                key={d.id}
                requirement={{ id: null, code: d.code, title: d.title, description: 'Additional supporting document', required: false, category: null, tracks: [] }}
                doc={d}
                editable={editable}
                onChanged={onChanged}
                onError={onError}
              />
            ))}
          </div>
        </section>
      )}

      <Card className="mt-5" title="Add a supporting document" subtitle="Anything else that strengthens your file — contracts, order book, bank sanction letters.">
        <div className="flex flex-wrap gap-2">
          <label htmlFor="extra-doc-title" className="sr-only">Supporting document title</label>
          <input id="extra-doc-title" value={extraTitle} onChange={(e) => setExtraTitle(e.target.value)} placeholder="Document title, e.g. Top customer contracts" className={`${FIELD} max-w-md flex-1`} style={FIELD_STYLE} />
          {extraTitle.trim().length >= 3 && (
            <UploadButton
              code={`EXTRA_${extraTitle.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0, 30)}`}
              title={extraTitle.trim()}
              label="Choose file"
              primary
              onUploaded={() => {
                setExtraTitle('');
                onChanged();
              }}
              onError={onError}
            />
          )}
        </div>
      </Card>
    </div>
  );
}
